import GraphemerImport from 'graphemer';
import { Node as SlateNode, Text as SlateText } from 'slate';

import {
  HIGHLIGHT_MARK,
  type HighlightMark,
  type HighlightMarkType,
  normalizeMarkup,
} from 'src/data/markedText';
import { unwrapDefault } from 'src/lib/cjsInterop';

const Graphemer = unwrapDefault(GraphemerImport);
// One splitter for the module: it holds no per-call state, and these run on every keystroke.
const splitter = new Graphemer();

import type { CustomSlateElement, CustomSlateLeaf, CustomSlateNode, SlateValue } from './types';

/**
 * Marks over a value of blocks, as `markedSerialization` does over a value of plain paragraphs.
 *
 * A mark's `paragraph` counts **non-empty** top-level blocks, because that is what the backend
 * counts: `TextComparisonService` numbers by `doc.css('body > *')` and skips an element with no
 * text. An empty paragraph and a divider therefore take no index. Offsets inside a block are
 * grapheme counts over its text descendants joined, so a `<br>` (one `\n` text node) counts as one
 * character, the way the backend counts the space it converts that tag into.
 */

type MarkTypeByGrapheme = (HighlightMarkType | undefined)[];

/** A mark as the wire carries it: `ContentMarkup.type` is a plain string there. */
export type StoredHighlightMark = Omit<HighlightMark, 'type'> & { type: string };

const HIGHLIGHT_MARK_TYPES: string[] = Object.values(HIGHLIGHT_MARK);

// A type the editor has no rendering for would still reach the DOM as a `data-mark`, so the wire's
// string is narrowed by asking whether it names a mark rather than by asserting that it does.
const isHighlightMark = (mark: StoredHighlightMark): mark is HighlightMark =>
  HIGHLIGHT_MARK_TYPES.includes(mark.type);

const blockText = (block: CustomSlateElement): string => SlateNode.string(block);

/** The blocks a mark's `paragraph` can point at, in the order it numbers them. */
const markedBlocks = (value: SlateValue): CustomSlateElement[] =>
  value.filter((block) => blockText(block) !== '');

const markTypesByGrapheme = (marks: HighlightMark[]): MarkTypeByGrapheme => {
  const types: MarkTypeByGrapheme = [];
  marks.forEach(({ type, start, end }) => {
    for (let index = start; index < end; index += 1) {
      types[index] = type;
    }
  });
  return types;
};

const withMark = (
  leaf: CustomSlateLeaf,
  text: string,
  mark?: HighlightMarkType
): CustomSlateLeaf => {
  const { mark: previousMark, ...rest } = leaf;
  return mark ? { ...rest, text, mark } : { ...rest, text };
};

/** Cuts one leaf wherever the mark type changes, keeping its other properties on every piece. */
const splitLeafByMarks = (
  leaf: CustomSlateLeaf,
  graphemes: string[],
  types: MarkTypeByGrapheme,
  offset: number
): CustomSlateLeaf[] => {
  if (graphemes.length === 0) {
    return [withMark(leaf, leaf.text)];
  }

  const pieces: CustomSlateLeaf[] = [];
  let runStart = 0;
  for (let index = 1; index <= graphemes.length; index += 1) {
    const runMark = types[offset + runStart];
    const nextMark = index < graphemes.length ? types[offset + index] : undefined;
    if (index === graphemes.length || runMark !== nextMark) {
      pieces.push(withMark(leaf, graphemes.slice(runStart, index).join(''), runMark));
      runStart = index;
    }
  }
  return pieces;
};

/**
 * Walks a block's descendants in reading order. `cursor` carries the grapheme offset across the
 * whole block, so a leaf nested in a list item is measured from the block's start, not its own.
 */
const applyToNode = (
  node: CustomSlateNode,
  types: MarkTypeByGrapheme,
  cursor: { offset: number }
): CustomSlateNode[] => {
  if (SlateText.isText(node)) {
    const leaf = node as CustomSlateLeaf;
    // Split and advance from one pass, so the pieces and the offset cannot disagree.
    const graphemes = splitter.splitGraphemes(leaf.text);
    const pieces = splitLeafByMarks(leaf, graphemes, types, cursor.offset);
    cursor.offset += graphemes.length;
    return pieces;
  }

  const element = node as CustomSlateElement;
  return [
    {
      ...element,
      children: element.children.flatMap((child) => applyToNode(child, types, cursor)),
    },
  ];
};

/**
 * Puts stored marks onto a value's leaves, replacing whatever marks it already carried. The value
 * keeps its structure and its formatting — only text nodes are cut, at the mark boundaries.
 */
export const applyMarkupToSlateValue = (
  value: SlateValue,
  markup?: StoredHighlightMark[] | null
): SlateValue => {
  const stripped = stripMarksFromSlateValue(value);
  if (!markup || markup.length === 0) {
    return stripped;
  }

  const normalized = normalizeMarkup(markup.filter(isHighlightMark));
  const blocks = markedBlocks(stripped);
  const typesByBlock = new Map<CustomSlateElement, MarkTypeByGrapheme>(
    blocks.map((block, index) => [
      block,
      markTypesByGrapheme(normalized.filter((mark) => mark.paragraph === index)),
    ])
  );

  return stripped.map((block) => {
    const types = typesByBlock.get(block);
    if (!types || types.length === 0) {
      return block;
    }
    return applyToNode(block, types, { offset: 0 })[0] as CustomSlateElement;
  });
};

const readBlockMarks = (block: CustomSlateElement, paragraph: number): HighlightMark[] => {
  const marks: HighlightMark[] = [];
  let offset = 0;

  // `SlateNode.texts` yields the block's text descendants in reading order.
  for (const [node] of SlateNode.texts(block)) {
    const leaf = node as CustomSlateLeaf;
    const length = splitter.countGraphemes(leaf.text);
    if (leaf.mark) {
      marks.push({ type: leaf.mark, paragraph, start: offset, end: offset + length });
    }
    offset += length;
  }

  return marks;
};

/**
 * Reads back where the marks now sit, after the editor has carried them through the writer's edits.
 * Adjacent pieces of one run are merged: formatting inside a marked run splits the leaf without
 * splitting the mark.
 *
 * Only the improve's own marks are returned. The `user` mark exists to stop inserted text inheriting
 * the mark of the leaf it lands in, which it does while the editor is open; nothing renders it, and
 * storing it would grow the saved markup by one run per edited passage for no reader.
 */
export const readMarkupFromSlateValue = (value: SlateValue): HighlightMark[] => {
  const marks = markedBlocks(value).flatMap((block, index) => readBlockMarks(block, index));
  return normalizeMarkup(marks).filter((mark) => mark.type === HIGHLIGHT_MARK.recompose);
};

const mergeableLeaves = (left: CustomSlateLeaf, right: CustomSlateLeaf): boolean => {
  const { text: _leftText, ...leftProps } = left;
  const { text: _rightText, ...rightProps } = right;
  const keys = Object.keys(leftProps);
  return (
    keys.length === Object.keys(rightProps).length &&
    keys.every(
      (key) =>
        leftProps[key as keyof typeof leftProps] === rightProps[key as keyof typeof rightProps]
    )
  );
};

const stripMarksFromNode = (node: CustomSlateNode): CustomSlateNode => {
  if (SlateText.isText(node)) {
    const { mark: _mark, ...rest } = node as CustomSlateLeaf;
    return rest;
  }

  const element = node as CustomSlateElement;
  const children = element.children.map(stripMarksFromNode).reduce((merged, child) => {
    const previous = merged[merged.length - 1];
    if (
      previous &&
      SlateText.isText(previous) &&
      SlateText.isText(child) &&
      mergeableLeaves(previous as CustomSlateLeaf, child as CustomSlateLeaf)
    ) {
      merged[merged.length - 1] = {
        ...previous,
        text: previous.text + (child as CustomSlateLeaf).text,
      };
      return merged;
    }
    merged.push(child);
    return merged;
  }, [] as CustomSlateNode[]);

  return { ...element, children };
};

const nodeCarriesMark = (node: CustomSlateNode): boolean =>
  SlateText.isText(node)
    ? Boolean((node as CustomSlateLeaf).mark)
    : (node as CustomSlateElement).children.some(nodeCarriesMark);

/**
 * Drops every mark and rejoins the leaves it split. Serializing a marked value to HTML would
 * otherwise emit one `<strong>` run per piece where the unmarked value emits a single tag, which
 * reads downstream as a content change the writer never made.
 *
 * An unmarked value is returned as it is: there is nothing to drop, and nothing to rejoin either,
 * because only a mark boundary splits a leaf that the editor would otherwise keep whole. This is
 * the common case — every part of an asset that no improve has touched — and it runs on each
 * throttled keystroke, so it is worth not copying the document for.
 */
export const stripMarksFromSlateValue = (value: SlateValue): SlateValue =>
  value.some(nodeCarriesMark)
    ? value.map((block) => stripMarksFromNode(block) as CustomSlateElement)
    : value;
